import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { CalendarClock, Pencil, Plus, RotateCcw, Send, Share2, Trash2, Undo2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type RowAction,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { SocialBulkResult, SocialPost } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { displayTimeZone, useStore } from '@/shared/store/store-context';

import { BulkOutcomeAlert } from '../components/bulk-actions';
import { usePostColumns } from '../components/post-columns';
import { postFilters } from '../components/post-filters';
import { RemovePostDialog } from '../components/remove-post-dialog';
import { ScheduleDialog } from '../components/schedule-dialog';
import { usePostBulkActions } from '../hooks/use-post-bulk-actions';
import {
  useBulkSocialPosts,
  usePublishSocialPost,
  useUnscheduleSocialPost,
} from '../hooks/use-post-mutations';
import { useSocialPosts } from '../hooks/use-social-posts';
import { scopedPostParams } from '../schemas/post-list-search';
import { canPublishPost, displayPostTitle } from '../schemas/post-state';

const ROUTE_ID = '/_authed/_active/s/$storeId/social/pinterest/posts/' as const;
const ROUTE_PATH = '/s/$storeId/social/pinterest/posts/' as const;

const PROVIDER = 'pinterest';

/** What the schedule dialog is open for: one post (reschedule) or a bulk selection. */
type ScheduleTarget = { kind: 'one'; post: SocialPost } | { kind: 'many'; rows: SocialPost[] };

function NewPostButton({ storeId }: { storeId: string }) {
  const { t } = useTranslation('social');
  return (
    <Button asChild>
      <Link to="/s/$storeId/social/pinterest/create" params={{ storeId }}>
        <Plus aria-hidden />
        {t('posts.new')}
      </Link>
    </Button>
  );
}

function Posts() {
  const { t } = useTranslation('social');
  const store = useStore();
  const format = useFormat();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const message = useErrorMessage();
  const { can } = usePermissions();
  const canWrite = can('social.write') && store.status !== 'closed';
  const publish = usePublishSocialPost(store.id);
  const unschedule = useUnscheduleSocialPost(store.id);
  const bulk = useBulkSocialPosts(store.id);
  const [removing, setRemoving] = useState<SocialPost | null>(null);
  const [scheduling, setScheduling] = useState<ScheduleTarget | null>(null);
  const [bulkResult, setBulkResult] = useState<{
    result: SocialBulkResult;
    rows: SocialPost[];
  } | null>(null);
  const timeZone = displayTimeZone(store.timezone);

  const filters = useMemo(() => postFilters(t), [t]);
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const params = useMemo(() => scopedPostParams(list.params, PROVIDER), [list.params]);
  const query = useSocialPosts(store.id, params);
  const rows = query.data?.items ?? [];

  const open = useCallback(
    (row: SocialPost) =>
      void navigate({
        to: '/s/$storeId/social/pinterest/posts/$postId',
        params: { storeId: store.id, postId: row.id },
      }),
    [navigate, store.id],
  );

  const publishMutate = publish.mutateAsync;
  const publishOne = useCallback(
    async (row: SocialPost, publishAt?: string) => {
      try {
        const saved = await publishMutate({ id: row.id, body: publishAt ? { publishAt } : {} });
        toast.success(
          publishAt && saved.publishAt
            ? t('toasts.postScheduled', { when: format.dateTime(saved.publishAt) })
            : t('toasts.postQueued'),
        );
      } catch (error) {
        toast.error(message(error));
        throw error;
      }
    },
    [format, message, publishMutate, t],
  );

  const bulkMutate = bulk.mutateAsync;
  const runBulk = useCallback(
    async (
      selected: SocialPost[],
      action: 'publish' | 'schedule' | 'discard',
      publishAt?: string,
    ) => {
      const ids = selected.map((row) => row.id);
      if (ids.length === 0) return;
      try {
        const result = await bulkMutate({ ids, action, ...(publishAt ? { publishAt } : {}) });
        setBulkResult({ result, rows: selected });
      } catch (error) {
        toast.error(message(error));
        throw error;
      }
    },
    [bulkMutate, message],
  );

  const bulkActions = usePostBulkActions({
    canWrite,
    onPublish: (selected) => runBulk(selected, 'publish'),
    onSchedule: (selected) => setScheduling({ kind: 'many', rows: selected }),
    onDiscard: (selected) => runBulk(selected, 'discard'),
  });

  const rowActions = useMemo<Array<RowAction<SocialPost>>>(
    () => [
      {
        id: 'open',
        label: t('actions.open'),
        icon: <Pencil aria-hidden />,
        onAction: (row) => open(row),
      },
      {
        id: 'publish',
        label: t('actions.publishNow'),
        icon: <Send aria-hidden />,
        separatorBefore: true,
        hidden: (row) => !canWrite || row.status !== 'draft',
        onAction: (row) => publishOne(row).catch(() => undefined),
      },
      {
        id: 'retry',
        label: t('actions.retry'),
        icon: <RotateCcw aria-hidden />,
        separatorBefore: true,
        hidden: (row) => !canWrite || row.status !== 'failed',
        onAction: (row) => publishOne(row).catch(() => undefined),
      },
      {
        id: 'schedule',
        label: t('actions.schedule'),
        icon: <CalendarClock aria-hidden />,
        hidden: (row) => !canWrite || row.status !== 'draft',
        onAction: (row) => setScheduling({ kind: 'one', post: row }),
      },
      {
        id: 'reschedule',
        label: t('actions.reschedule'),
        icon: <CalendarClock aria-hidden />,
        separatorBefore: true,
        hidden: (row) => !canWrite || row.status !== 'scheduled',
        onAction: (row) => setScheduling({ kind: 'one', post: row }),
      },
      {
        id: 'unschedule',
        label: t('actions.unschedule'),
        icon: <Undo2 aria-hidden />,
        hidden: (row) => !canWrite || row.status !== 'scheduled',
        onAction: (row) =>
          unschedule.mutateAsync(row.id).then(
            () => undefined,
            () => undefined,
          ),
      },
      {
        id: 'remove',
        label: t('actions.remove'),
        icon: <Trash2 aria-hidden />,
        variant: 'destructive',
        separatorBefore: true,
        hidden: (row) => !canWrite || row.status === 'removed',
        disabled: (row) => row.status === 'publishing',
        onAction: (row) => setRemoving(row),
      },
    ],
    [canWrite, open, publishOne, t, unschedule],
  );

  const columns = usePostColumns(store.id);
  const table = useDataTable({
    mode: 'server',
    data: rows,
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'social-posts',
  });

  const narrowed = Boolean(list.q) || Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && rows.length === 0 && !narrowed;
  const scheduleInitial =
    scheduling?.kind === 'one' && scheduling.post.status === 'scheduled'
      ? scheduling.post.publishAt
      : null;

  return (
    <PageContent>
      <PageHeader
        title={t('posts.title')}
        description={t('posts.description', { store: store.name, network: t(`${PROVIDER}.name`) })}
        actions={
          <Can permission="social.write">
            <NewPostButton storeId={store.id} />
          </Can>
        }
      />
      {bulkResult ? (
        <BulkOutcomeAlert
          result={bulkResult.result}
          rows={bulkResult.rows}
          onDismiss={() => setBulkResult(null)}
        />
      ) : null}
      {isEmptyList ? (
        <EmptyState
          icon={Share2}
          title={t('posts.empty.title')}
          description={t('posts.empty.description')}
          action={
            <Can permission="social.write">
              <NewPostButton storeId={store.id} />
            </Can>
          }
        />
      ) : (
        <DataTable
          table={table}
          search={{ placeholder: t('posts.search'), label: t('posts.search') }}
          loading={query.isPending}
          fetching={query.isFetching || publish.isPending || bulk.isPending || unschedule.isPending}
          error={query.error}
          onRetry={() => void query.refetch()}
          bulkActions={bulkActions}
          rowActions={rowActions}
          onRowClick={(row) => open(row)}
          getRowLabel={displayPostTitle}
          resourceName={{ singular: t('posts.resource.one'), plural: t('posts.resource.other') }}
          emptyState={{ title: t('posts.noMatches') }}
        />
      )}
      <RemovePostDialog
        storeId={store.id}
        row={removing}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
      />
      <ScheduleDialog
        open={scheduling !== null}
        onOpenChange={(open) => {
          if (!open) setScheduling(null);
        }}
        title={
          scheduling?.kind === 'many'
            ? t('schedule.bulkTitle', { count: scheduling.rows.length })
            : t('schedule.title')
        }
        description={t('schedule.description', { network: t(`${PROVIDER}.name`) })}
        timeZone={timeZone}
        initial={scheduleInitial}
        submitting={publish.isPending || bulk.isPending}
        onSubmit={async (publishAt) => {
          if (!scheduling) return;
          if (scheduling.kind === 'one') {
            if (!canPublishPost(scheduling.post.status)) return;
            await publishOne(scheduling.post, publishAt);
          } else {
            await runBulk(scheduling.rows, 'schedule', publishAt);
          }
        }}
      />
    </PageContent>
  );
}

export function PostsPage() {
  return (
    <FeatureErrorBoundary name="social.posts">
      <Posts />
    </FeatureErrorBoundary>
  );
}
